<template>
  <div class="service-overview">
    <load-profile />

    <div class="order-container">
      <div class="order-item">
        <div class="icon-container">
          <NewOrderIcon />
        </div>
        <div>
          <h5>{{ getTotalNewOrders() }}</h5>
          <p>{{ $t('general.today_new_orders') }}</p>
        </div>
      </div>

      <div class="order-item">
        <div class="icon-container">
          <CompletedOrderIcon />
        </div>
        <div>
          <h5>{{ getTotalCompletedOrders() }}</h5>
          <p>{{ $t('general.today_completed_orders') }}</p>
        </div>
      </div>

      <div class="order-item">
        <div class="icon-container">
          <PendingOrderIcon />
        </div>
        <div>
          <h5>{{ getTotalPendingOrders() }}</h5>
          <p>{{ $t('general.pending_orders') }}</p>
        </div>
      </div>

      <div class="order-item">
        <div class="icon-container">
          <NewOrderWeekIcon />
        </div>
        <div>
          <h5>{{ getTotalNewOrdersThisWeek() }}</h5>
          <p>{{ $t('general.this_week_new_orders') }}</p>
        </div>
      </div>

      <div class="order-item">
        <div class="icon-container">
          <CompletedOrderWeekIcon />
        </div>
        <div>
          <h5>{{ getTotalCompletedOrdersThisWeek() }}</h5>
          <p>{{ $t('general.this_week_completed_orders') }}</p>
        </div>
      </div>

      <div class="order-item">
        <div class="icon-container">
          <ThisMonthWorkOrderIcon />
        </div>
        <div>
          <h5>{{ getTotalWorkOrders() }}</h5>
          <p>{{ $t('general.this_month_work_orders') }}</p>
        </div>
      </div>

      <div class="order-item">
        <div class="icon-container">
          <ThisMonthCompletedWorkOrderIcon />
        </div>
        <div>
          <h5>{{ getTotalCompletedWorkOrders() }}</h5>
          <p>{{ $t('general.this_month_completed_work_orders') }}</p>
        </div>
      </div>

      <div class="order-item">
        <div class="icon-container">
          <IconThisMonthMaintenanceAmount />
        </div>
        <div>
          <h5>{{ getTotalMaintenanceAmount() }}</h5>
          <p>{{ $t('general.this_month_maintenance_amount') }}</p>
        </div>
      </div>
    </div>

    <div class="card-data-layout">
      <div class="left-content">
        <div class="card-data-container">
          <h2 class="card-data-header">
            {{ $t('general.pending_service_orders') }}
          </h2>

          <b-table
            :items="pendingOrders"
            :fields="fields"
            responsive
            :busy="isLoading || loading"
            show-empty
            :empty-text="$t('general.noRecord')"
            :empty-filtered-text="$t('general.noRecord')"
            class="custom-table list-table"
          >
            <template #table-colgroup>
              <col
                v-for="field in fields"
                :key="field.key"
                :style="getColumnStyle(field)"
              >
            </template>

            <template #cell(serviceType)="data">

              {{ getServiceType(data.item.serviceType.name) }}
            </template>

            <template #cell(orderNumber)="data">
              {{ data.item.orderNumber || '-' }}
            </template>

            <template #cell(orderInitiator)="data">
              <div class="d-flex align-items-center">
                <b-avatar
                  :src="data.item.meta && data.item.meta.profileUrl"
                  size="2rem"
                  variant="primary"
                  class="mr-1"
                />
                <div>
                  <div>
                    {{ (data.item.meta && data.item.meta.name) || '-' }}
                  </div>
                </div>
              </div>
            </template>

            <template #cell(initiateTime)="data">
              {{ data.item.createdAt | formatDate }}
            </template>

            <template #cell(status)="data">
              <span :class="data.item.status">
                {{ $t(`status.${data.item.status}`) }}
              </span>
            </template>

            <template #cell(operation)="data">
              <b-button
                v-if="$can('manage', 'manage-modify-service')"
                size="sm"
                variant="primary"
                @click="handleAssign(data.item)"
              >
                {{ $t('button.assign') }}
              </b-button>
            </template>

            <template #table-busy>
              <div class="text-center my-1">
                <b-skeleton-table
                  :rows="10"
                  :columns="fields.length"
                  :hide-header="true"
                  :table-props="{ bordered: false, striped: true }"
                />
              </div>
            </template>
          </b-table>
        </div>

        <div class="card-data-container mt-2">
          <div>
            <div class="card-data-header header-actions">
              <h2 class="card-data-header-text">
                {{ $t('general.service_orders_data') }}
              </h2>
              <div class="filter-date-container">
                <div class="action-buttons">
                  <div
                    v-for="filter in serviceOrderDataFilter"
                    :key="filter.value"
                    :class="{
                      active: activeServiceOrderDataFilter === filter.value
                    }"
                    @click="handleFilterClick(filter)"
                  >
                    {{ filter.text }}
                  </div>
                </div>

                <!-- Date picker dropdown for custom filter -->
                <div
                  v-if="
                    activeServiceOrderDataFilter === 'custom' &&
                      showCustomDateDropdown
                  "
                  class="custom-date-dropdown"
                >
                  <div class="date-input">
                    <label>{{ $t('field.fromDate') }}</label>
                    <n-date-picker
                      v-model="orderTypeFilter.startDate"
                      :clearable="true"
                    />
                  </div>
                  <div class="date-input">
                    <label>{{ $t('field.toDate') }}</label>
                    <n-date-picker
                      v-model="orderTypeFilter.endDate"
                      :clearable="true"
                    />
                  </div>
                  <div class="date-actions">
                    <b-button
                      variant="primary"
                      size="md"
                      :disabled="
                        !orderTypeFilter.startDate || !orderTypeFilter.endDate
                      "
                      @click="handleCustomDateChange"
                    >
                      {{ $t('button.confirm') || 'Confirm' }}
                    </b-button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Single loading indicator for the entire Service Orders Data section -->
          <div
            v-if="isLoadingOrderData"
            class="section-loading-container"
          >
            <div
              class="spinner-border text-primary"
              role="status"
            >
              <span class="sr-only">{{ $t('general.loading') }}</span>
            </div>
            <p class="mt-2">
              {{ $t('general.loading') }}
            </p>
          </div>

          <div v-else>
            <div class="card-data-sub-container">
              <h3 class="card-data-sub-header order">
                {{ $t('general.service_order_type') }}
              </h3>
              <div class="circle-chat">
                <div class="chart-container">
                  <div class="chart-section">
                    <div class="total-orders">
                      <h2>{{ totalOrders || 0 }}</h2>
                      <p>{{ $t('general.total_orders') }}</p>
                    </div>
                    <doughnut-chart
                      :key="chartKey"
                      :chart-data="serviceTypeData"
                      :options="chartOptions"
                      :height="140"
                    />
                  </div>
                  <div class="chart-legend">
                    <div
                      v-for="(item, index) in serviceTypeData.labels"
                      :key="index"
                      class="legend-item"
                    >
                      <div
                        class="legend-dot"
                        :style="{
                          backgroundColor:
                            serviceTypeData.datasets[0].backgroundColor[index]
                        }"
                      />
                      <div class="legend-text">
                        {{ translateLegendItem(item) }}
                      </div>
                      <div
                        v-if="item !== $t('general.no_data')"
                        class="legend-value"
                      >
                        {{
                          serviceTypeData.datasets[0].data[index]
                            ? serviceTypeData.datasets[0].data[index] + '%'
                            : $t('general.no_data')
                        }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="sub-chart-container">
                <div
                  v-for="(
                    orderBreakdown, index
                  ) in orderData.orderStatusBreakdown"
                  :key="index"
                >
                  <h3 class="card-data-sub-header">
                    {{ getNameByLang(orderBreakdown[0]) }}
                  </h3>
                  <div class="progress-wrapper">
                    <div class="progress-container">
                      <div
                        v-for="(progressItem, progressIndex) in orderBreakdown"
                        :key="progressIndex"
                        class="progress-row"
                      >
                        <div class="progress-bar-wrapper">
                          <div
                            v-if="
                              Math.round(Number(progressItem.percentage)) > 0
                            "
                            class="progress-bar"
                            :style="{
                              width: calculateProgressWidth(progressItem),
                              backgroundColor: getColorByStatus(
                                progressItem.status
                              )
                            }"
                          >
                            <span
                              class="progress-text"
                            >{{
                              Math.round(Number(progressItem.percentage))
                            }}%</span>
                          </div>
                        </div>
                        <span class="progress-label">{{
                          $t(`status.${progressItem.status}`)
                        }}</span>
                        <span class="progress-count">{{
                          progressItem.count
                        }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="card-data-container mt-2">
          <!-- <h2 class="card-data-header">
            {{ $t('general.daily_service_orders') }}
          </h2> -->
          <div>
            <div class="card-data-header header-actions">
              <h2 class="card-data-header-text">
                {{ $t('general.daily_service_orders') }}
              </h2>
              <div class="filter-date-container">
                <div class="action-buttons">
                  <div
                    v-for="filter in dailyServiceOrderFilter"
                    :key="filter.value"
                    :class="{
                      active: activeDailyServiceOrderFilter === filter.value
                    }"
                    @click="handleDailyOrderFilterClick(filter)"
                  >
                    {{ filter.text }}
                  </div>
                </div>

                <!-- Date picker dropdown for custom filter -->
                <div
                  v-if="
                    activeDailyServiceOrderFilter === 'custom' &&
                      showDailyCustomDateDropdown
                  "
                  class="custom-date-dropdown"
                >
                  <div class="date-input">
                    <label>{{ $t('field.fromDate') }}</label>
                    <n-date-picker
                      v-model="dailyOrderFilter.startDate"
                      :clearable="true"
                    />
                  </div>
                  <div class="date-input">
                    <label>{{ $t('field.toDate') }}</label>
                    <n-date-picker
                      v-model="dailyOrderFilter.endDate"
                      :clearable="true"
                    />
                  </div>
                  <div class="date-actions">
                    <b-button
                      variant="primary"
                      size="md"
                      :disabled="
                        !dailyOrderFilter.startDate || !dailyOrderFilter.endDate
                      "
                      @click="handleDailyCustomDateChange"
                    >
                      {{ $t('button.confirm') || 'Confirm' }}
                    </b-button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Single loading indicator for the Daily Service Orders section -->
          <div
            v-if="isLoading"
            class="section-loading-container"
          >
            <div
              class="spinner-border text-primary"
              role="status"
            >
              <span class="sr-only">Loading...</span>
            </div>
            <p class="mt-2">
              {{ $t('general.loading') }}
            </p>
          </div>

          <div
            v-else
            class="card-data-sub-container"
          >
            <div class="daily-chart">
              <bar-chart
                :key="chartKey"
                :chart-data="dailyOrdersData"
                :options="dailyOrdersOptions"
                :height="300"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="right-content">
        <div class="card-data-container">
          <div>
            <div class="card-data-header header-actions">
              <h2 class="card-data-header-text">
                {{ $t('general.operator_ranking') }}
              </h2>
              <!-- <div class="action-text">
                {{ $t('general.more') }}
                <feather-icon
                  icon="ChevronRightIcon"
                  size="20"
                />
              </div> -->
            </div>
          </div>

          <!-- Single loading indicator for the Operator Ranking section -->
          <!-- <div
            v-if="isLoading"
            class="section-loading-container"
          >
            <div
              class="spinner-border text-primary"
              role="status"
            >
              <span class="sr-only">Loading...</span>
            </div>
            <p class="mt-2">
              {{ $t('general.loading') }}
            </p>
          </div> -->

          <b-table
            :items="rankingList"
            :fields="rankingFields"
            responsive
            :busy="isLoading"
            show-empty
            :empty-text="$t('general.noRecord')"
            :empty-filtered-text="$t('general.noRecord')"
            class="custom-table table-ranking"
          >
            <template #cell(rank)="data">
              <span :class="getRankClass(data.item.rank)">
                {{ data.item.rank }}
              </span>
            </template>
            <template #cell(duration)="data">
              <span>
                {{ secondsToDHMS(data.item.totalDurationMs / 1000) }}
              </span>
            </template>
            <template #table-busy>
              <div class="text-center my-1">
                <b-skeleton-table
                  :rows="10"
                  :columns="fields.length"
                  :hide-header="true"
                  :table-props="{ bordered: false, striped: true }"
                />
              </div>
            </template>
          </b-table>
        </div>
      </div>
    </div>

    <update-status-modal
      ref="updateStatusChatModal"
      :active-channel="activeChannel"
      @success="onUpdateStatusSuccess"
    />
  </div>
</template>

<script>
import NewOrderIcon from '@/components/icons/NewOrderIcon.vue'
import {
  BTable, BButton, BAvatar, BSkeletonTable,
} from 'bootstrap-vue'
import { Doughnut, Bar } from 'vue-chartjs'
import Repository from '@/repositories/RepositoryFactory'
import UpdateStatusModal from '@/views/service/v2/components/UpdateStatusModal.vue'
import { groupBy } from 'lodash'
import NDatePicker from '@/components/NDatePicker.vue'
import CompletedOrderIcon from '@/components/icons/CompletedOrderIcon.vue'
import PendingOrderIcon from '@/components/icons/PendingOrderIcon.vue'
import NewOrderWeekIcon from '@/components/icons/NewOrderWeekIcon.vue'
import CompletedOrderWeekIcon from '@/components/icons/CompletedOrderWeekIcon.vue'
import ThisMonthWorkOrderIcon from '@/components/icons/ThisMonthWorkOrderIcon.vue'
import ThisMonthCompletedWorkOrderIcon from '@/components/icons/ThisMonthCompletedWorkOrderIcon.vue'
import IconThisMonthMaintenanceAmount from '@/components/icons/IconThisMonthMaintenanceAmount.vue'
import { secondsToDHMS } from '@/libs/helper'

const ServiceSystemRepository = Repository.get('serviceSystem')

const DoughnutChart = {
  extends: Doughnut,
  props: {
    chartData: {
      type: Object,
      required: true,
    },
    options: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isUpdating: false,
    }
  },
  watch: {
    chartData: {
      handler() {
        if (this.isUpdating) return

        this.isUpdating = true
        this.$nextTick(() => {
          if (this.hasValidData()) {
            this.renderChart(this.chartData, this.options)
          } else {
            this.renderFallback()
          }
          this.isUpdating = false
        })
      },
      deep: true,
    },
    options: {
      handler() {
        if (this.isUpdating) return

        this.isUpdating = true
        this.$nextTick(() => {
          if (this.hasValidData()) {
            this.renderChart(this.chartData, this.options)
          } else {
            this.renderFallback()
          }
          this.isUpdating = false
        })
      },
      deep: true,
    },
  },
  methods: {
    hasValidData() {
      return (
        this.chartData
          && this.chartData.datasets
          && this.chartData.datasets.length > 0
          && this.chartData.datasets[0].data
          && this.chartData.datasets[0].data.length > 0
      )
    },
    renderFallback() {
      this.$nextTick(() => {
        const { canvas } = this.$refs
        if (canvas) {
          const ctx = canvas.getContext('2d')
          ctx.clearRect(0, 0, canvas.width, canvas.height)
          ctx.font = '14px Arial'
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.fillStyle = '#6E6B7B'
          ctx.fillText(
            this.$root.$t('general.no_data'),
            canvas.width / 2,
            canvas.height / 2,
          )
        }
      })
    },
  },
  mounted() {
    this.isUpdating = true
    this.$nextTick(() => {
      if (this.hasValidData()) {
        this.renderChart(this.chartData, this.options)
      } else {
        this.renderFallback()
      }
      this.isUpdating = false
    })
  },
}

const BarChart = {
  extends: Bar,
  props: {
    chartData: {
      type: Object,
      required: true,
    },
    options: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isUpdating: false,
    }
  },
  watch: {
    chartData: {
      handler() {
        if (this.isUpdating) return

        this.isUpdating = true
        this.$nextTick(() => {
          if (this.hasValidData()) {
            this.renderChart(this.chartData, this.options)
          } else {
            this.renderFallback()
          }
          this.isUpdating = false
        })
      },
      deep: true,
    },
    options: {
      handler() {
        if (this.isUpdating) return

        this.isUpdating = true
        this.$nextTick(() => {
          if (this.hasValidData()) {
            this.renderChart(this.chartData, this.options)
          } else {
            this.renderFallback()
          }
          this.isUpdating = false
        })
      },
      deep: true,
    },
  },
  methods: {
    hasValidData() {
      return (
        this.chartData
          && this.chartData.datasets
          && this.chartData.datasets.length > 0
          && this.chartData.datasets[0].data
          && this.chartData.datasets[0].data.length > 0
      )
    },
    renderFallback() {
      this.$nextTick(() => {
        const { canvas } = this.$refs
        if (canvas) {
          const ctx = canvas.getContext('2d')
          ctx.clearRect(0, 0, canvas.width, canvas.height)
          ctx.font = '14px Arial'
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.fillStyle = '#6E6B7B'
          ctx.fillText(
            this.$root.$t('general.no_data'),
            canvas.width / 2,
            canvas.height / 2,
          )
        }
      })
    },
  },
  mounted() {
    this.isUpdating = true
    this.$nextTick(() => {
      this.renderChart(this.chartData, this.options)
      this.isUpdating = false
    })
  },
}

export default {
  components: {
    NewOrderIcon,
    BTable,
    BButton,
    BAvatar,
    DoughnutChart,
    BarChart,
    UpdateStatusModal,
    BSkeletonTable,
    NDatePicker,
    CompletedOrderIcon,
    PendingOrderIcon,
    NewOrderWeekIcon,
    CompletedOrderWeekIcon,
    ThisMonthWorkOrderIcon,
    ThisMonthCompletedWorkOrderIcon,
    IconThisMonthMaintenanceAmount,
  },
  data() {
    return {
      orderTypeFilter: {
        startDate: '',
        endDate: '',
      },
      dailyOrderFilter: {
        startDate: '',
        endDate: '',
      },
      showCustomDateDropdown: false,
      showDailyCustomDateDropdown: false,
      activeChannel: {},
      activeServiceOrderDataFilter: 'all',
      activeDailyServiceOrderFilter: 'last_7_days',
      isLoading: false,
      isLoadingOrderData: false,
      loading: false,
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        cutoutPercentage: 75,
        legend: {
          display: false,
        },
        tooltips: {
          enabled: false,
        },
      },
      serviceTypeData: {
        labels: [],
        datasets: [],
      },
      chartKey: 0,
      dailyOrdersData: {
        labels: [
          '12-01',
          '12-02',
          '12-03',
          '12-04',
          '12-05',
          '12-06',
          '12-07',
        ],
        datasets: [
          {
            label: 'New Orders',
            backgroundColor: '#44C1FF',
            data: [100, 180, 50, 180, 100, 240, 130],
          },
          {
            label: 'Completed Orders',
            backgroundColor: '#4BC0C0',
            data: [150, 240, 100, 100, 150, 100, 200],
          },
        ],
      },
      dailyOrdersOptions: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          xAxes: [
            {
              gridLines: {
                display: false,
                drawBorder: false,
              },
              ticks: {
                fontColor: '#6E6B7B',
              },
            },
          ],
          yAxes: [
            {
              gridLines: {
                color: '#F3F2F7',
                drawBorder: false,
                zeroLineColor: '#F3F2F7',
              },
              ticks: {
                stepSize: 50,
                min: 0,
                max: 250,
                fontColor: '#6E6B7B',
              },
            },
          ],
        },
        legend: {
          display: true,
          position: 'bottom',
          labels: {
            boxWidth: 10,
            usePointStyle: true,
            padding: 20,
            fontColor: '#6E6B7B',
          },
        },
        tooltips: {
          backgroundColor: '#F3F2F7',
          titleFontColor: '#6E6B7B',
          bodyFontColor: '#6E6B7B',
          borderColor: '#E4E4E4',
          borderWidth: 1,
        },
      },
      // dynamic data
      summaryData: {},
      pendingOrders: [],
      orderData: {
        serviceOrderTypes: [],
        orderStatusBreakdown: [],
      },
      dailyOrders: [],
      rankingList: [],
    }
  },
  computed: {
    progressData() {
      // Create progress data with sorting by percentage in descending order
      const data = [
        {
          percentage: 55,
          label: 'Pending',
          count: 50,
          color: '#1973F9',
        },
        {
          percentage: 25,
          label: 'Completed',
          count: 25,
          color: '#FFCC00',
        },
        {
          percentage: 20,
          label: 'Returned',
          count: 25,
          color: '#48B35E',
        },
      ]

      // Sort by percentage in descending order
      return data.sort((a, b) => b.percentage - a.percentage)
    },
    serviceOrderDataFilter() {
      return [
        {
          text: this.$t('general.all'),
          value: 'all',
        },
        {
          text: this.$t('general.last_7_days'),
          value: 'last_7_days',
        },
        {
          text: this.$t('general.this_month'),
          value: 'this_month',
        },
        {
          text: this.$t('general.custom'),
          value: 'custom',
        },
      ]
    },
    dailyServiceOrderFilter() {
      return [
        {
          text: this.$t('general.last_7_days'),
          value: 'last_7_days',
        },
        {
          text: this.$t('general.this_month'),
          value: 'this_month',
        },
        {
          text: this.$t('general.custom'),
          value: 'custom',
        },
      ]
    },
    fields() {
      return [
        {
          key: 'serviceType',
          label: this.$t('breadcrumb.serviceType'),
          class: 'text-nowrap',
        },
        {
          key: 'orderNumber',
          label: this.$t('general.order_number'),
          class: 'text-nowrap',
        },
        {
          key: 'status',
          label: this.$t('field.status'),
          class: 'text-nowrap',
        },
        {
          key: 'orderInitiator',
          label: this.$t('general.order_initiator'),
          class: 'text-nowrap',
        },
        {
          key: 'initiateTime',
          label: this.$t('general.initiate_time'),
          class: 'text-nowrap',
        },
        {
          key: 'operation',
          label: this.$t('general.operation'),
          class: 'text-nowrap',
          width: 120,
          tdClass: 'sticky-column sticky-service-actions',
          thClass: 'sticky-header sticky-header-actions',
          thStyle: {
            right: '0px',
            background: 'white !important',
          },
        },
      ]
    },
    rankingFields() {
      return [
        {
          key: 'rank',
          label: this.$t('general.rank'),
          class: 'text-nowrap',
        },
        {
          key: 'operator',
          label: this.$t('ticket.operator'),
          class: 'text-nowrap',
        },
        {
          key: 'quantity',
          label: this.$t('field.order_qty'),
          class: 'text-nowrap',
        },
        {
          key: 'duration',
          label: this.$t('general.duration'),
          class: 'text-nowrap',
        },
      ]
    },
    totalOrders() {
      return this.orderData.serviceOrderTypes.reduce(
        (acc, curr) => acc + Number(curr.count),
        0,
      )
    },
  },
  watch: {
    '$i18n.locale': function () {
      this.getDailyOrders(false)
    },
    activeServiceOrderDataFilter(newValue) {
      // Reset the dropdown state when switching away from custom filter
      if (newValue !== 'custom') {
        this.showCustomDateDropdown = false
      }
    },
    activeDailyServiceOrderFilter(newValue) {
      // Reset the dropdown state when switching away from custom filter
      if (newValue !== 'custom') {
        this.showDailyCustomDateDropdown = false
      }
    },
  },
  created() {
    this.dailyOrderFilter = this.getLast7Days()
    this.fetchOverviewData()
  },
  methods: {
    getServiceType(serviceTypeName) {
      return (
          serviceTypeName?.[this.$i18n.locale.split('-')[0]]
          || serviceTypeName?.en
          || serviceTypeName?.zh
          || serviceTypeName?.km
          || serviceTypeName?.vi
          || '-'
      )
    },
    getColumnStyle(field) {
      if (field.width) {
        return {
          width: `${field.width}px`,
          minWidth: `${field.width}px`,
          maxWidth: `${field.width}px`,
        }
      }
      return {}
    },
    getTotalWorkOrders() {
      return this.summaryData?.totalServiceOrderThisMonth || 0
    },
    getTotalCompletedWorkOrders() {
      return this.summaryData?.totalCompletedServiceOrderThisMonth || 0
    },
    getTotalMaintenanceAmount() {
      return this.summaryData?.totalPricingMaintenanceAmountThisMonth?.totalUSD || 0
    },
    getTotalCompletedOrdersThisWeek() {
      return this.summaryData?.completedOrderThisWeek || 0
    },
    getTotalNewOrdersThisWeek() {
      return this.summaryData?.newOrderThisWeek || 0
    },
    getTotalPendingOrders() {
      return this.summaryData?.pendingOrder || 0
    },
    getTotalNewOrders() {
      return this.summaryData?.todayNewOrder || 0
    },
    getTotalCompletedOrders() {
      return this.summaryData?.todayCompletedOrder || 0
    },
    async onUpdateStatusSuccess() {
      this.loading = true
      try {
        this.$refs.updateStatusChatModal.hide()
        const pending = await ServiceSystemRepository.getPendingOrders({
          page: 1,
        })
        this.pendingOrders = pending.data?.data?.list || []
      } catch (error) {
        //
      } finally {
        this.loading = false
      }
    },
    handleAssign(data) {
      this.activeChannel = data
      this.$refs.updateStatusChatModal.show(data, 'restart', 'assignOperator')
    },
    getNameByLang(data) {
      if (!data?.name) return 'N/A'
      return data.name?.[this.$i18n.locale] || data.name?.zh || data.name?.en || data.name?.km || data.name?.vi || 'N/A'
    },
    generateUniqueColors(count) {
      // Primary colors from the provided image - these will be assigned to the highest percentage items
      const primaryColors = [
        '#F05454', // Red from image - for highest percentage
        '#F8A055', // Orange from image - for second highest
        '#5DADE2', // Blue from image - for third highest
      ]

      // If we only need these three colors, return them
      if (count <= primaryColors.length) {
        return primaryColors.slice(0, count)
      }

      // Secondary palette for additional colors if needed
      const secondaryColors = [
        '#4285F4',
        '#EA4335',
        '#FBBC05',
        '#34A853', // Google colors
        '#3498DB',
        '#E74C3C',
        '#2ECC71',
        '#F39C12', // Flat UI colors
        '#9C27B0',
        '#673AB7',
        '#3F51B5',
        '#2196F3', // Material colors
        '#03A9F4',
        '#00BCD4',
        '#009688',
        '#4CAF50',
        '#8BC34A',
        '#CDDC39',
        '#FFEB3B',
        '#FFC107',
        '#FF9800',
        '#FF5722',
        '#795548',
        '#9E9E9E',
      ]

      // Combine primary and secondary colors
      const allColors = [...primaryColors, ...secondaryColors]

      // If we have enough colors in our combined palette, use them
      if (count <= allColors.length) {
        return allColors.slice(0, count)
      }

      // Otherwise, generate additional unique colors
      const colors = [...allColors]
      const usedColors = new Set(colors)

      while (colors.length < count) {
        const newColor = this.getRandomHexColor()
        if (!usedColors.has(newColor)) {
          colors.push(newColor)
          usedColors.add(newColor)
        }
      }

      return colors
    },
    async getOrderData() {
      // Only set loading to true if not already loading (to avoid conflicts with fetchOverviewData)
      const wasNotLoading = !this.isLoadingOrderData
      if (wasNotLoading) {
        this.isLoadingOrderData = true
      }

      try {
        const orderData = await ServiceSystemRepository.getOrderData(
          this.orderTypeFilter,
        )
        this.orderData = {
          serviceOrderTypes: orderData.data?.data?.serviceOrderTypes || [],
          orderStatusBreakdown: orderData.data?.data?.orderStatusBreakdown
            .length
            ? groupBy(
                  orderData.data?.data?.orderStatusBreakdown,
                  'serviceType',
            )
            : [],
        }

        // Re-map orderStatusBreakdown to ensure all statuses are included and properly sorted
        const requiredStatuses = [
          'pending',
          'accepted',
          'processing',
          'pendingPrice',
          'completed',
          'pendingDispatch',
          'cancel',
          'resolved',
        ]

        if (
          this.orderData.orderStatusBreakdown
            && Object.keys(this.orderData.orderStatusBreakdown).length
        ) {
          const remappedBreakdown = {}

          Object.keys(this.orderData.orderStatusBreakdown).forEach(
            serviceTypeKey => {
              const serviceTypeData = this.orderData.orderStatusBreakdown[serviceTypeKey]
              const existingStatuses = serviceTypeData.map(
                item => item.status,
              )

              // Create a list with only valid statuses from the existing data
              const filteredList = serviceTypeData.filter(item => requiredStatuses.includes(item.status))

              // Add missing statuses with count 0 and percentage 0
              requiredStatuses.forEach(status => {
                if (!existingStatuses.includes(status)) {
                  filteredList.push({
                    ...serviceTypeData[0], // Copy service type info from first item
                    status,
                    count: 0,
                    percentage: 0,
                  })
                }
              })

              // Sort by the required status order
              const sortedList = filteredList.sort(
                (a, b) => requiredStatuses.indexOf(a.status)
                    - requiredStatuses.indexOf(b.status),
              )

              remappedBreakdown[serviceTypeKey] = sortedList
            },
          )

          this.orderData.orderStatusBreakdown = remappedBreakdown
        }

        const serviceTypes = this.orderData.serviceOrderTypes || []

        if (!serviceTypes.length) {
          this.serviceTypeData = {
            labels: [this.$t('general.no_data')],
            datasets: [
              {
                data: [100],
                backgroundColor: ['#E0E0E0'],
                borderWidth: 0,
              },
            ],
          }
          this.chartKey += 1
          return
        }

        // Sort service types by percentage in descending order
        const sortedServiceTypes = [...serviceTypes].sort((a, b) => {
          const percentA = Math.round(Number(a.percentage || 0))
          const percentB = Math.round(Number(b.percentage || 0))
          return percentB - percentA
        })

        const uniqueColors = this.generateUniqueColors(
          sortedServiceTypes.length,
        )

        // Create a new object to ensure reactivity
        this.serviceTypeData = {
          labels: sortedServiceTypes.map(item => item),
          datasets: [
            {
              data: sortedServiceTypes.map(item => Math.round(Number(item.percentage || 0))),
              backgroundColor: uniqueColors,
              borderWidth: 0,
            },
          ],
        }

        // Increment the key to force re-rendering
        this.chartKey += 1
      } catch (error) {
        console.error('Error fetching order data:', error)
      } finally {
        // Only set loading to false if we set it to true
        if (wasNotLoading) {
          this.isLoadingOrderData = false
        }
      }
    },
    getDailyOrders(includeLoading = true) {
      try {
        if (includeLoading) this.isLoading = true
        ServiceSystemRepository.getDailyOrders(this.dailyOrderFilter)
          .then(response => {
            const dailyData = response.data?.data?.list || []

            if (!dailyData.length) {
              this.dailyOrdersData = {
                labels: [],
                datasets: [
                  {
                    label: this.$t('general.new_orders'),
                    backgroundColor: '#44C1FF',
                    data: [],
                  },
                  {
                    label: this.$t('general.completed_orders'),
                    backgroundColor: '#4BC0C0',
                    data: [],
                  },
                ],
              }
              this.chartKey += 1
              return
            }

            // Sort data by date
            const sortedData = [...dailyData].sort((a, b) => {
              // Use bracket notation to access properties with underscores
              const dateA = new Date(a['id']) // eslint-disable-line dot-notation
              const dateB = new Date(b['id']) // eslint-disable-line dot-notation
              return dateA - dateB
            })

            // Format dates for display
            const labels = sortedData.map(item => {
              // Use bracket notation to access properties with underscores
              const date = new Date(item['id']) // eslint-disable-line dot-notation
              return `${date.getMonth() + 1}-${date.getDate()}`
            })

            // Extract data for new orders and completed orders
            const newOrdersData = sortedData.map(item => item.newOrders || 0)
            const completedOrdersData = sortedData.map(
              item => item.completedOrders || 0,
            )

            // Find the maximum value to set appropriate y-axis scale
            const maxValue = Math.max(
              ...newOrdersData,
              ...completedOrdersData,
              100, // Minimum scale value
            )

            // Update chart options with new max value
            this.dailyOrdersOptions.scales.yAxes[0].ticks.max = Math.ceil(
              maxValue * 1.2,
            ) // Add 20% padding

            console.log('newOrdersData', newOrdersData)
            console.log('completedOrdersData', completedOrdersData)
            // Update chart data
            this.dailyOrdersData = {
              labels,
              datasets: [
                {
                  label: this.$t('general.new_orders'),
                  backgroundColor: '#44C1FF',
                  data: newOrdersData,
                },
                {
                  label: this.$t('general.completed_orders'),
                  backgroundColor: '#4BC0C0',
                  data: completedOrdersData,
                },
              ],
            }

            // Force chart to re-render
            this.chartKey += 1
          })
          .catch(error => {
            console.error('Error fetching daily orders data:', error)
          })
          .finally(() => {
            this.isLoading = false
          })
      } catch (error) {
        console.error('Error in getDailyOrders:', error)
        this.isLoading = false
      }
    },
    async fetchOverviewData() {
      try {
        this.isLoading = true
        const [summary, pending, ranking] = await Promise.all([
          ServiceSystemRepository.getOverviewSummary({ page: 1 }),
          ServiceSystemRepository.getPendingOrders({ page: 1 }),
          ServiceSystemRepository.getOperatorRanking({ limit: 10 }),
        ])

        await this.getOrderData()
        await this.getDailyOrders()

        // Handle the responses
        this.summaryData = summary.data?.data || {}
        this.pendingOrders = pending.data?.data?.list || []

        // Add rank property to each item in the ranking list
        this.rankingList = (ranking.data?.data?.list || []).map(
          (item, index) => ({
            ...item,
            rank: index + 1,
          }),
        )
      } catch (error) {
        console.error('Error fetching overview data:', error)
      } finally {
        this.isLoading = false
      }
    },
    getRankClass(rank) {
      return {
        'rank-badge': true,
        'rank-top': rank <= 3,
        'rank-other': rank > 3,
      }
    },
    getStatusVariant(status) {
      const variants = {
        pending: 'warning',
        processing: 'info',
        completed: 'success',
        cancelled: 'danger',
      }
      return variants[status] || 'secondary'
    },
    getColorByStatus(status) {
      const colors = {
        pending: '#FFA500', // Orange for pending
        processing: '#1976D2', // Updated blue for processing (more visible with white text)
        resolved: '#008000', // Green for resolved
        pendingPrice: '#FFD700', // Gold for pending price
        hold: '#808080', // Gray for hold
        rejected: '#FF0000', // Red for rejected
        unknown: '#A9A9A9', // Dark gray for unknown
        cancel: '#FF4500', // OrangeRed for cancel
        pendingDispatch: '#1E90FF', // DodgerBlue for pending dispatch
        accepted: '#32CD32', // LimeGreen for accepted
      }
      return colors[status] || '#FFCC00' // Default color
    },
    calculateProgressWidth(progressItem) {
      // Get the percentage value
      const percentage = Math.round(Number(progressItem.percentage))

      // Ensure small percentages (like 1%) are still visible
      if (percentage <= 5) {
        return '5%' // Minimum width for visibility
      }

      return `${percentage}%`
    },
    viewOrder(order) {
      // Handle view order action
      this.$router.push(`/service/${order.id}`)
    },
    getLast7Days() {
      const endDate = new Date()
      const startDate = new Date()
      startDate.setDate(endDate.getDate() - 7)
      return {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      }
    },
    getThisMonth() {
      const endDate = new Date()
      const startDate = new Date()
      startDate.setDate(1)
      return {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      }
    },
    handleOrderTypeFilter(filter) {
      if (
        filter === 'all'
          && this.orderTypeFilter.startDate
          && this.orderTypeFilter.endDate
      ) {
        this.orderTypeFilter = {
          startDate: '',
          endDate: '',
        }
      } else if (filter === 'last_7_days') {
        this.orderTypeFilter = this.getLast7Days()
      } else if (filter === 'this_month') {
        this.orderTypeFilter = this.getThisMonth()
      }
      this.getOrderData()
    },
    handleCustomDateChange() {
      // Only fetch data if both dates are selected
      if (this.orderTypeFilter.startDate && this.orderTypeFilter.endDate) {
        this.getOrderData()

        // Close the dropdown but keep the custom filter active
        this.showCustomDateDropdown = false
      }
    },
    handleFilterClick(filter) {
      this.activeServiceOrderDataFilter = filter.value

      if (filter.value === 'custom') {
        this.showCustomDateDropdown = !this.showCustomDateDropdown
      } else {
        this.handleOrderTypeFilter(filter.value)
      }
    },
    handleDailyOrderFilter(filter) {
      if (filter === 'last_7_days') {
        this.dailyOrderFilter = this.getLast7Days()
      } else if (filter === 'this_month') {
        this.dailyOrderFilter = this.getThisMonth()
      } else if (filter === 'custom') {
        // Custom filter will be handled by date picker
        return
      }
      this.getDailyOrders()
    },
    handleDailyOrderFilterClick(filter) {
      this.activeDailyServiceOrderFilter = filter.value

      if (filter.value === 'custom') {
        this.showDailyCustomDateDropdown = !this.showDailyCustomDateDropdown
      } else {
        this.handleDailyOrderFilter(filter.value)
      }
    },
    handleDailyCustomDateChange() {
      // Only fetch data if both dates are selected
      if (this.dailyOrderFilter.startDate && this.dailyOrderFilter.endDate) {
        this.getDailyOrders()

        // Close the dropdown but keep the custom filter active
        this.showDailyCustomDateDropdown = false
      }
    },
    translateLegendItem(item) {
      // If it's already a translated "no data" message, return it as is
      if (item === this.$t('general.no_data')) {
        return item
      }

      // For other items, they're already translated by getNameByLang when creating the chart
      return this.getNameByLang(item)
    },
  },
  setup() {
    return {
      secondsToDHMS,
    }
  },
}
</script>

<style lang="scss">
  .custom-table {
    &.list-table {
      position: relative;
      overflow-x: auto;

      .sticky-service-status {
        right: 310px;
      }

      .sticky-service-actions {
        right: 0;
        border-left: 1px solid var(--light);
      }

      .sticky-service-status,
      .sticky-header {
        border-left: 1px solid var(--light);
        border-right: 1px solid var(--light);
      }

      .sticky-header-actions {
        border-left: 1px solid var(--light);
        border-right: none !important;
      }

      td.sticky-column,
      th.sticky-header {
        position: sticky !important;
        background: white !important;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      td.sticky-column {
        z-index: 1;
        // box-shadow: -2px 0 4px rgba(0, 0, 0, 0.1);
      }

      th.sticky-header {
        z-index: 2;
        // box-shadow: -2px 0 4px rgba(0, 0, 0, 0.1);
      }
    }
    .table thead th {
      background: transparent !important;
      color: #6b6873 !important;
      text-transform: capitalize;
      font-weight: 600;
      font-size: 1rem;
      padding: 1.5rem 2rem 1rem 2rem !important;
    }

    .table td {
      border-top: none !important;
      padding: 0.72rem 2rem !important;
    }
  }
</style>

<style lang="scss" scoped>
  .service-overview {
    .progress-container {
      background: #f6f4fa;
      padding: 1rem;
      border-radius: 0.5rem;
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .progress-row {
      display: flex;
      align-items: center;
      gap: 1rem;
    }

    .progress-bar-wrapper {
      width: 150px;
      height: 18px;
      background: #fff;
      border-radius: 0.25rem;
      overflow: hidden;
      display: flex;
      align-items: center;
    }

    .progress-bar {
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: flex-start;
      color: white;
      font-size: 10px;
      font-weight: bold;
      border-radius: 0.25rem;
      min-width: 25px;
      padding-left: 5px;
      padding-top: 2px;
    }

    .progress-text {
      padding: 0 1px;
      color: white;
      text-shadow: 0px 0px 2px rgba(0, 0, 0, 0.5);
      white-space: nowrap;
    }

    .progress-label {
      flex: 0.8;
      color: #6b6873;
      font-size: 12px;
    }

    .progress-count {
      font-size: 12px;
      color: #6b6873;
    }

    .rank-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border-radius: 0.5rem;
      color: white;
      font-weight: 600;
      font-size: 0.875rem;
    }

    .rank-top {
      background-color: #f8ac28;
    }

    .rank-other {
      background-color: #b9b9b9;
    }

    .order-container {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 2rem;
      padding: 1.5rem 0;
      background: white;
      border-radius: 0.5rem;

      // Optional: Add responsive behavior
      @media (max-width: 1200px) {
        grid-template-columns: repeat(3, 1fr);
      }

      @media (max-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
      }

      @media (max-width: 576px) {
        grid-template-columns: repeat(1, 1fr);
      }

      @media (max-width: 400px) {
        grid-template-columns: 1fr;
      }

      .order-item {
        display: flex;
        align-items: center;
        gap: 1rem;

        .icon-container {
          display: flex;
          justify-content: center;
          align-items: center;
          border-radius: 0.5rem;
          padding: 1rem;
        }

        &:nth-child(1) .icon-container {
          background: #006be5;
        }

        &:nth-child(2) .icon-container {
          background: #48b35e;
        }

        &:nth-child(3) .icon-container {
          background: #ffcc00;
        }

        &:nth-child(4) .icon-container {
          background: #44c1ff;
        }

        &:nth-child(5) .icon-container {
          background: #00ccaa;
        }

        &:nth-child(6) .icon-container {
          background: #FF8800;
        }

        &:nth-child(7) .icon-container {
          background: #50B5B5;
        }

        &:nth-child(8) .icon-container {
          background: #5858DA;
        }
      }
    }

    .card-data-layout {
      display: grid;
      grid-template-columns: repeat(6, 1fr);
      gap: 1.5rem;
      margin-top: 1.5rem;

      .left-content {
        grid-column: span 4;
      }

      .right-content {
        grid-column: span 2;
      }

      @media (max-width: 1200px) {
        .right-content {
          grid-column: span 4;
        }
      }

      .card-data-container {
        background: white;
        border-radius: 0.75rem;
        border: 0.5px solid #dddddd;

        &:first-child {
          grid-column: span 3;
        }

        &:last-child {
          grid-column: span 2;
        }

        .card-data-header {
          padding: 1.5rem 1rem;
          margin-bottom: 0;
          font-size: 1.2rem;
          border-top-left-radius: 0.75rem;
          border-top-right-radius: 0.75rem;
          color: #5e5873;
          background: #f7f7f7;
          // border-bottom: 1px solid #ebe9f1;

          .card-data-header-text {
            font-size: 1.2rem;
            margin-bottom: 0;
          }

          &.header-actions {
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-bottom: 1px solid #ebe9f1;
            position: relative;

            .action-buttons {
              display: flex;
              gap: 0.5rem;
              font-size: 1rem;
              font-weight: 500;
              cursor: pointer;
              position: relative;

              .active {
                background: #f2eff6;
                color: #8d5185;
                border: 1px solid #8d5185;
              }

              & > div {
                border: 1px solid #ebe9f1;
                border-radius: 0.5rem;
                min-width: 110px;
                padding: 0.5rem;
                text-align: center;
                background: white;
              }
            }

            .filter-date-container {
              position: relative;
            }

            .action-text {
              color: var(--primary);
              margin-right: -0.5rem;
              cursor: pointer;
              font-size: 1rem;
              font-weight: 500;
            }
          }
        }

        .card-data-sub-container {
          background: white;
          border-bottom-left-radius: 0.75rem;
          border-bottom-right-radius: 0.75rem;

          .daily-chart {
            padding: 1rem;
            height: 350px;
          }

          .circle-chat {
            padding: 1rem;

            .chart-container {
              display: flex;
              align-items: center;
              gap: 2rem;
            }

            .chart-section {
              position: relative;
              width: 160px;
              height: 140px;
              flex-shrink: 0;
            }

            .total-orders {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              text-align: center;
              z-index: 1;

              h2 {
                font-size: 1.5rem;
                margin: 0;
                font-weight: 600;
                color: #5e5873;
              }

              p {
                margin: 0;
                font-size: 0.75rem;
                color: #6e6b7b;
              }
            }

            .chart-legend {
              flex: 1;
              max-width: 260px;

              .legend-item {
                display: grid;
                grid-template-columns: auto 1fr 40px;
                align-items: center;
                margin-bottom: 0.75rem;
                gap: 0.5rem;

                &:last-child {
                  margin-bottom: 0;
                }

                .legend-dot {
                  width: 8px;
                  height: 8px;
                  border-radius: 50%;
                }

                .legend-text {
                  color: #6e6b7b;
                  font-size: 1rem;
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                }

                .legend-value {
                  font-weight: 500;
                  color: #5e5873;
                  text-align: right;
                }
              }
            }
          }

          .card-data-sub-header {
            &.order {
              padding: 1rem;
            }

            padding: 0 0 1.5rem 0;
            margin-bottom: 0;
            font-size: 1rem;
            border-top-left-radius: 0.75rem;
            border-top-right-radius: 0.75rem;
            color: #5e5873;
          }

          .sub-chart-container {
            padding: 1rem;
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 1rem;
            margin-top: 1rem;

            // Optional: Add responsive behavior
            @media (max-width: 768px) {
              grid-template-columns: repeat(2, 1fr);
            }

            @media (max-width: 576px) {
              grid-template-columns: 1fr;
            }

            > div {
              background: white;
              // padding: 1rem;
            }
          }
        }
      }

      // Responsive layout
      @media (max-width: 1200px) {
        grid-template-columns: 1fr;

        .card-data-container {
          &:first-child,
          &:last-child {
            grid-column: 1; // Stack on mobile
          }
        }
      }
    }

    .pending {
      color: #ffc107;
    }

    .pendingDispatch {
      color: #f39c12; /* New color for pending dispatch */
    }

    .bg-pending {
      background-color: #ffc107;
    }

    .bg-pendingDispatch {
      background-color: #f39c12; /* New color for pending dispatch */
    }

    .processing {
      color: rgb(0, 123, 255);
    }

    .bg-processing {
      background-color: rgb(0, 123, 255);
    }

    .cancel {
      color: #c0392b;
    }

    .bg-cancel {
      background-color: #c0392b;
    }

    .unresolved {
      color: #ff8c00;
    }

    .bg-unresolved {
      background-color: #ff8c00;
    }

    .hold {
      color: #8d5185;
    }

    .bg-hold {
      background-color: #8d5185;
    }

    .completed {
      color: #27ae60;
    }

    .resolved {
      color: #7e7e7e;
    }

    .bg-completed {
      background-color: #27ae60;
    }

    .bg-assign {
      background-color: #97b834;
    }

    .bg-resolved {
      background-color: #7e7e7e;
    }

    .bg-handover {
      background-color: var(--primary);
    }

    .handover {
      color: var(--primary);
    }

    .rejected {
      color: #e74c3c;
    }

    .bg-rejected {
      background-color: #e74c3c;
    }

    .bg-accepted {
      background-color: #2ecc71;
    }

    .bg-pendingPrice {
      background-color: #f1c40f;
    }

    .accepted {
      color: #2ecc71;
    }

    .pendingPrice {
      color: #f1c40f;
    }

    /* Loading indicators */
    .chart-loading {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: rgba(255, 255, 255, 0.7);
      border-radius: 50%;
    }

    .chart-loading-container {
      grid-column: 1 / -1;
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 200px;
      background-color: rgba(255, 255, 255, 0.7);
      border-radius: 0.5rem;
    }

    .section-loading-container {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      min-height: 300px;
      background-color: rgba(255, 255, 255, 0.7);
      border-radius: 0.5rem;
      margin: 1rem 0;

      p {
        margin-top: 1rem;
        color: var(--primary);
        font-weight: 500;
      }
    }

    .custom-date-dropdown {
      padding: 1rem;
      background: #f8f8f8;
      border-radius: 0.5rem;
      border: 1px solid #ebe9f1;
      margin: 0;
      position: absolute;
      z-index: 10;
      width: 100%;
      right: 0;
      top: calc(100% + 5px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    .date-range-container {
      display: flex;
      gap: 1rem;

      @media (max-width: 576px) {
        flex-direction: column;
      }
    }

    .date-input {
      flex: 1;
      margin-bottom: 1rem;

      label {
        display: block;
        margin-bottom: 0.5rem;
        font-size: 0.875rem;
        color: #5e5873;
      }

      ::v-deep .date-picker {
        width: 100%;
      }
    }

    .date-actions {
      display: flex;
      justify-content: flex-end;
      margin-top: 0.5rem;
    }

    .btn-confirm {
      background-color: var(--primary);
      color: white;
      border: none;
      border-radius: 0.5rem;
      padding: 0.5rem 1.5rem;
      font-weight: 500;
      cursor: pointer;
      transition: background-color 0.2s;

      &:hover {
        background-color: darken(#8d5185, 10%);
      }

      &:disabled {
        background-color: #cccccc;
        cursor: not-allowed;
      }
    }

    .spinner-border {
      width: 3rem;
      height: 3rem;
    }
  }
</style>
